"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Button from "../../../../components/Button";
import { FaTrash } from "react-icons/fa";
import { fetchClinicScriptDetails as fetchClinicScriptDetailsApi } from "../../../../api/dispensary/script/clinicScriptApi.js";
import styles from "../../../Clinic/Consultations/css/invoice.module.css";

export default function ClinicScriptInvoice() {
  const { scriptNo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [scriptData, setScriptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const patientInfo = location.state || {};

  const [lineItems, setLineItems] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchScript = async () => {
      if (!scriptNo) return;
      try {
        setLoading(true);
        setError("");
        const data = await fetchClinicScriptDetailsApi(scriptNo);
        if (cancelled) return;

        if (!data) {
          setError("Script not found");
          return;
        }

        const clinicRef = data.script?.clinicreference || data.script?.clinicReference || "";

        const services = Array.isArray(data?.script?.services) ? data.script.services : [];
        
        // Deduplicate services based on serviceid or servicecode
        const uniqueServices = services.filter((service, index, self) => 
          index === self.findIndex((s) => 
            (s?.serviceid && s?.serviceid === service?.serviceid) || 
            (s?.servicecode && s?.servicecode === service?.servicecode)
          )
        );
        
        const itemsFromServices = uniqueServices.map((svc, index) => {
          const unitPrice = Number(svc?.price) || 0;
          const qty = Number(svc?.quantity) || 1;
          return {
            id: `${svc?.serviceid || svc?.servicecode || `service-${index}-${Date.now()}`}`,
            code: svc?.servicecode || "",
            description: svc?.servicename || "Service",
            quantity: qty,
            unitPrice,
            total: unitPrice * qty,
            locked: true,
          };
        });

        setScriptData({
          scriptNumber: clinicRef || data.script?.id || scriptNo,
          date: data.script?.date || "",
          patient: data.patient || null,
          drugs: Array.isArray(data.drugs) ? data.drugs : [],
          services,
        });
        setLineItems(itemsFromServices);
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || "Failed to load clinic script invoice");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchScript();

    return () => {
      cancelled = true;
    };
  }, [scriptNo]);

  const handleDelete = (id) => {
    setLineItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleChange = (id, field, value) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        if (item.locked) return item;
        const updated = { ...item, [field]: value };
        updated.total = (Number(updated.quantity) || 0) * (Number(updated.unitPrice) || 0);
        return updated;
      })
    );
  };

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + (Number(item.total) || 0), 0);
  };

  const patientName =
    patientInfo?.patientName ||
    `${scriptData?.patient?.firstname || ""} ${scriptData?.patient?.surname || ""}`.trim() ||
    "Patient";

  if (loading) {
    return (
      <div className={styles.pageWrapper}>
        <Navbar />
        <main className={styles.mainContent}>
          <div className={styles.container}>
            <h1 className={styles.title}>Invoice</h1>
            <p className={styles.subtitle}>Loading invoice...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageWrapper}>
        <Navbar />
        <main className={styles.mainContent}>
          <div className={styles.container}>
            <h1 className={styles.title}>Invoice</h1>
            <p className={styles.subtitle}>{error}</p>
            <div className={styles.finalizeWrapper}>
              <Button variant="primary" type="button" onClick={() => navigate(-1)}>
                Back
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <Navbar />

      <main className={styles.mainContent}>
        <div className={styles.container}>
          <h1 className={styles.title}>Invoice</h1>
          <p className={styles.subtitle}>Consolidated invoice for {patientName}&apos;s visit</p>

          <div className={styles.card}>
            <h3 className={styles.sectionTitle}>Line Items</h3>

            <div className={styles.tableHeader}>
              <span>Code</span>
              <span>Description</span>
              <span>Qty</span>
              <span>Unit Price</span>
              <span>Total</span>
              <span></span>
            </div>

            {lineItems.map((item) => (
              <div key={item.id} className={styles.row}>
                <span>{item.code}</span>
                <span>{item.description}</span>

                <input
                  type="number"
                  value={item.quantity}
                  onChange={(e) => handleChange(item.id, "quantity", parseInt(e.target.value) || 0)}
                  readOnly={!!item.locked}
                />

                <input
                  type="number"
                  value={item.unitPrice}
                  onChange={(e) => handleChange(item.id, "unitPrice", parseFloat(e.target.value) || 0)}
                  readOnly={!!item.locked}
                />

                <span className={styles.totalCell}>R{(Number(item.total) || 0).toFixed(2)}</span>

                {item.locked ? (
                  <span />
                ) : (
                  <button className={styles.deleteBtn} onClick={() => handleDelete(item.id)} type="button">
                    <FaTrash style={{ color: "red" }} />
                  </button>
                )}
              </div>
            ))}

            <div className={styles.totalRow}>
              <span>Total</span>
              <strong>R{calculateTotal().toFixed(2)}</strong>
            </div>
          </div>

          <div className={styles.finalizeWrapper}>
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate(`/customer/dispensary/clinic-script/details/${scriptNo}`, { state: patientInfo })}
              style={{ marginRight: "12px" }}
            >
              Back to Script
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
